import React, { FC, useCallback } from 'react';
import { useFormikContext } from 'formik';

import { FormInput } from 'Shared/components';
import { AddressType } from 'Shared/utils';
import { useAppContext } from 'QuoteApp/services/providers';
import { Suggestion } from 'Shared/hooks/useAddressLookup/useAddressLookup';
import { RequiredWithUndefined } from 'Shared/utils/types';

import { FormErrorBanner } from '../FormErrorBanner';
import { MailingAddressFormType, MailingAddressPropsType } from './types';

export const MailingAddress: FC<MailingAddressPropsType> = ({
  hasError = false,
  disabled = false,
  readOnly = false,
  tooltip = '',
}) => {
  const { details } = useAppContext();
  const { values, touched, errors, handleBlur, setValues, setFieldValue } =
    useFormikContext<MailingAddressFormType>();

  const fieldNamePrefix = `mailingAddress[${details.principalInsuredId}]`;

  const buildMailingAddressFormValues = (): AddressType => ({
    address1: values.mailingAddress[details.principalInsuredId].address1 ?? '',
    address2: values.mailingAddress[details.principalInsuredId].address2 ?? '',
    city: values.mailingAddress[details.principalInsuredId].city ?? '',
    state: values.mailingAddress[details.principalInsuredId].state,
    zip: values.mailingAddress[details.principalInsuredId].zip ?? '',
  });

  const isMissingAddressLine2 = details.mailingAddressOutcomes.isMissingLineTwo;

  const buildMailingAddressErrorMessages =
    (): RequiredWithUndefined<AddressType> => {
      return {
        address1: touched.mailingAddress?.[details.principalInsuredId]?.address1
          ? errors.mailingAddress?.[details.principalInsuredId]?.address1
          : undefined,
        address2:
          errors.mailingAddress?.[details.principalInsuredId]?.address2 ||
          isMissingAddressLine2
            ? 'Line 2 is required'
            : undefined,
        city: touched.mailingAddress?.[details.principalInsuredId]?.city
          ? errors.mailingAddress?.[details.principalInsuredId]?.city
          : undefined,
        state: touched.mailingAddress?.[details.principalInsuredId]?.state
          ? errors.mailingAddress?.[details.principalInsuredId]?.state
          : undefined,
        zip: touched.mailingAddress?.[details.principalInsuredId]?.zip
          ? errors.mailingAddress?.[details.principalInsuredId]?.zip
          : undefined,
      };
    };

  const setAddressFieldValue = useCallback(
    <K extends keyof AddressType>(key: K): ((value: AddressType[K]) => void) =>
      (value: AddressType[K]) => {
        setFieldValue(`${fieldNamePrefix}.${key}`, value);
      },
    [fieldNamePrefix, setFieldValue]
  );

  const overrideFormFieldValues = (selectedOption: Suggestion) => {
    const { city, state, streetLine, zipcode } = selectedOption;

    setValues((prevValues) => ({
      ...prevValues,
      mailingAddress: {
        ...prevValues.mailingAddress,
        [details.principalInsuredId]: {
          ...prevValues.mailingAddress[details.principalInsuredId],
          address1: streetLine,
          city: city,
          state: state,
          zip: zipcode,
        },
      },
    }));
  };

  const renderError = () => {
    return (
      <FormErrorBanner message="We’re sorry, we were unable to validate this address. Please enter a valid mailing address to continue." />
    );
  };

  return (
    <div data-testid="mailing-address-form">
      {hasError && renderError()}
      <FormInput
        variant="addressAutocomplete"
        prefix={fieldNamePrefix}
        values={buildMailingAddressFormValues()}
        onAddressSelect={overrideFormFieldValues}
        onAddressLine1Change={setAddressFieldValue('address1')}
        onAddressLine2Change={setAddressFieldValue('address2')}
        onCityChange={setAddressFieldValue('city')}
        onStateChange={setAddressFieldValue('state')}
        onZipChange={setAddressFieldValue('zip')}
        errorMessages={buildMailingAddressErrorMessages()}
        onBlur={handleBlur}
        disabled={disabled}
        readOnly={readOnly}
        tooltip={tooltip}
      />
    </div>
  );
};
